// Declarative skeleton of the scenario tree. Module 1 only *renders* the
// sections — Modules 2-7 will attach real sub-documents into the
// scenario parameters tree and deepen each section's field list.
//
// Every field reports:
//   name        — the dot-less key inside scenario.parameters[section]
//   label       — human label
//   kind        — one of the kinds in /src/lib/units.js
//   defaultUnit — preferred unit when no user pref is set
//   placeholder — example value for the empty state
//
// The actual <Parameter> row in Mongo ships with SI value + source +
// confidence + bounds + citation; <ParamField> pulls those through.

import {
    Plane,
    Cloud,
    Crosshair,
    Rocket,
    Bomb,
    PlayCircle,
    Mountain,
} from "lucide-react";

export const SECTIONS = [
    {
        id: "airframe",
        label: "Airframes",
        icon: Plane,
        description: "Powered platforms participating in the engagement (interceptors + targets).",
        fields: [
            { name: "mass", label: "Takeoff mass", kind: "mass", placeholder: "12.4" },
            { name: "max_thrust", label: "Max thrust", kind: "force", placeholder: "250" },
            { name: "wing_area", label: "Reference wing area", kind: "length", placeholder: "0.38" },
            { name: "max_speed", label: "VNE / dash speed", kind: "speed", placeholder: "95" },
            { name: "service_ceiling", label: "Service ceiling", kind: "altitude", placeholder: "5000" },
        ],
        backendHint: "Populated by Module 2 — JSBSim XML + airframe presets.",
    },
    {
        id: "environment",
        label: "Environment",
        icon: Cloud,
        description: "Atmosphere, wind, and turbulence field.",
        fields: [
            { name: "ground_temperature", label: "Ground temperature", kind: "temperature", placeholder: "288.15" },
            { name: "ground_pressure", label: "Ground pressure", kind: "pressure", placeholder: "101325" },
            { name: "wind_speed", label: "Wind speed @ 10m", kind: "speed", placeholder: "4.2" },
            { name: "wind_bearing", label: "Wind bearing", kind: "angle", placeholder: "270" },
            { name: "turbulence_intensity", label: "Turbulence intensity", kind: "dimensionless", placeholder: "0.08" },
        ],
        backendHint: "Populated by Module 3 — ISA + Dryden turbulence + preset library.",
    },
    {
        id: "terrain",
        label: "Terrain + LOS",
        icon: Mountain,
        description: "DEM + obstacles + RF line-of-sight preview (Module 4).",
        fields: [],
        backendHint:
            "Attach a DEM tile and up to N obstacle packs; /sim/scenarios/{id}/los evaluates TX→RX on demand.",
    },
    {
        id: "threats",
        label: "Threats",
        icon: Crosshair,
        description: "Target swarm, waypoints, evasive-manoeuvre profile.",
        fields: [
            { name: "n_targets", label: "Swarm size", kind: "dimensionless", placeholder: "3" },
            { name: "initial_altitude", label: "Ingress altitude", kind: "altitude", placeholder: "120" },
            { name: "dash_speed", label: "Dash speed", kind: "speed", placeholder: "35" },
            { name: "spacing", label: "Formation spacing", kind: "length", placeholder: "25" },
        ],
        backendHint: "Populated by Module 5 — threat waypoints, spacing, evasion laws.",
    },
    {
        id: "interceptor",
        label: "Interceptor",
        icon: Rocket,
        description: "Seeker, guidance law, and kinematic limits.",
        fields: [
            { name: "seeker_fov", label: "Seeker FOV (full angle)", kind: "angle", placeholder: "8" },
            { name: "max_turn_rate", label: "Max turn rate", kind: "angle", placeholder: "35" },
            { name: "nav_constant", label: "Navigation constant (PN)", kind: "dimensionless", placeholder: "3" },
            { name: "seeker_lock_range", label: "Seeker lock range", kind: "length", placeholder: "1500" },
        ],
        backendHint: "Populated by Module 6 — PP/PN/APN/CLOS + seeker lock-loss logic.",
    },
    {
        id: "engagement",
        label: "Engagement",
        icon: Bomb,
        description: "Warhead, fuzing, lethality threshold.",
        fields: [
            { name: "warhead_radius", label: "Warhead lethal radius", kind: "length", placeholder: "3.5", bounds: { min: 0.1, max: 50 } },
            { name: "fuze_delay", label: "Fuze delay", kind: "time", placeholder: "0.02", bounds: { min: 0, max: 0.25 } },
            { name: "pk_threshold", label: "P(k) threshold", kind: "dimensionless", placeholder: "0.7", bounds: { min: 0, max: 1 } },
        ],
        backendHint: "Populated by Module 7 — warhead + fuzing + salvo logic.",
    },
    {
        id: "runs",
        label: "Runs",
        icon: PlayCircle,
        description: "Monte Carlo runs executed against this scenario.",
        fields: [],
        backendHint: "The scenario header's Run button submits a run against this scenario. Runs back-link here automatically.",
    },
    {
        id: "optimisation",
        label: "Design Optimisation",
        icon: Crosshair,
        description: "Search the design space for a Pk-target configuration.",
        fields: [],
        // Special-cased in WorkbenchPage — renders WorkbenchOptimisationPanel
        // instead of the parameter editor. No fields[] because there are no
        // scenario-tree parameters to edit on this tab.
        is_optimisation: true,
        backendHint: "Opens a recommended-configuration card or kicks the composer if no study has been run yet.",
    },
];

/** Look up a section by id; returns null if unknown. */
export function sectionById(id) {
    return SECTIONS.find((s) => s.id === id) || null;
}
